import React from "react";
import { Link } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import type { MergedAmmoData } from "@/lib/types/weapon";
import { createSortableHeader, type FilterType } from "@/components/DataTable";
import type { FilterFn } from "@tanstack/react-table";
import { createFilterString, createFilterNumber } from "@/components/DataTable";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { WeaponImage } from "@/components/WeaponImage/WeaponImage";
import { formatDistance } from "@/lib/utils/format-distance";
import { formatTime } from "@/lib/utils/format-time";

// Column labels
export const WEAPON_COLUMN_LABELS: Record<string, string> = {
  name: "Ammo Name",
  descriptorName: "Ammo Descriptor",
  minMaxCategory: "Min/Max Category",
  heDamage: "HE Damage",
  heDamageRadius: "HE Damage Radius",
  suppress: "Suppress",
  suppressDamagesRadius: "Suppress Damage Radius",
  firesLeftToRight: "Fires Left to Right",
  groundMaxRange: "Ground Max Range",
  groundMinRange: "Ground Min Range",
  heliMaxRange: "Heli Max Range",
  heliMinRange: "Heli Min Range",
  planeMaxRange: "Plane Max Range",
  planeMinRange: "Plane Min Range",
  aimingTime: "Aiming Time",
  reloadTime: "Reload Time",
  salvoLength: "Salvo Length",
  timeBetweenSalvos: "Time Between Shots",
  ammunitionPerSalvo: "Ammunition Per Salvo",
  rateOfFire: "Rate of Fire",
  trueRateOfFire: "True Rate of Fire",
  supplyCostPerSalvo: "Supply Cost Per Salvo",
  staticAccuracy: "Static Accuracy",
  movingAccuracy: "Moving Accuracy",
  distanceToTarget: "Distance to Target",
  damageDropOff: "Damage Drop Off",
  damageFamily: "Damage Family",
  damageIndex: "Damage Index",
  piercingWeapon: "Piercing Weapon",
  tandemCharge: "Tandem Charge",
  isKinetic: "Is Kinetic",
  penetration: "Penetration",
  noiseMalus: "Noise Malus",
  shotsBeforeMaxNoise: "Shots Before Max Noise",
  numberOfSimultaneousProjectiles: "Number of Simultaneous Projectiles",
};

// Column type mapping
export const WEAPON_COLUMN_TYPES: Record<string, FilterType> = {
  // Numbers
  heDamage: "number",
  heDamageRadius: "number",
  suppress: "number",
  suppressDamagesRadius: "number",
  groundMaxRange: "number",
  groundMinRange: "number",
  heliMaxRange: "number",
  heliMinRange: "number",
  planeMaxRange: "number",
  planeMinRange: "number",
  aimingTime: "number",
  reloadTime: "number",
  salvoLength: "number",
  timeBetweenSalvos: "number",
  ammunitionPerSalvo: "number",
  rateOfFire: "number",
  trueRateOfFire: "number",
  supplyCostPerSalvo: "number",
  staticAccuracy: "number",
  movingAccuracy: "number",
  damageDropOff: "number",
  penetration: "number",
  noiseMalus: "number",
  shotsBeforeMaxNoise: "number",
  numberOfSimultaneousProjectiles: "number",
  // Strings
  name: "string",
  descriptorName: "string",
  minMaxCategory: "string",
  damageFamily: "string",
  damageIndex: "string",
  // Booleans
  firesLeftToRight: "boolean",
  distanceToTarget: "boolean",
  piercingWeapon: "boolean",
  tandemCharge: "boolean",
  isKinetic: "boolean",
};

// Filter functions
const filterString = createFilterString<MergedAmmoData>();
const filterNumber = createFilterNumber<MergedAmmoData>();

// Helper function to render boolean values
function renderBoolean(value: boolean | undefined) {
  if (value === undefined) return <span className="text-muted-foreground">-</span>;
  return value ? "Yes" : "No";
}

// Helper function to render optional string/number values
function renderOptionalValue(value: string | number | undefined | null) {
  if (value === undefined || value === null || value === "") {
    return <span className="text-muted-foreground">-</span>;
  }
  return String(value);
}

// Helper function to calculate column width based on label length
function calculateColumnWidth(label: string): number {
  const charWidth = 8.5; // pixels per character
  const padding = 24; // total left/right padding
  const sortIconSpace = 24; // space for sort indicator
  const minWidth = 80; // minimum width for very short labels
  
  return Math.max(minWidth, label.length * charWidth + padding + sortIconSpace);
}

// Helper function to render array values
function renderArray(value: any[] | undefined) {
  if (!value || value.length === 0) return <span className="text-muted-foreground">-</span>;
  return value.join(", ");
}

// Helper function to create a simple sortable column
function createSortableColumn(
  accessorKey: keyof MergedAmmoData,
  label: string,
  options?: { 
    cell?: (value: unknown, ammo: MergedAmmoData) => React.ReactNode;
    filterFn?: FilterFn<MergedAmmoData>;
    size?: number;
    meta?: Record<string, unknown>;
  }
): ColumnDef<MergedAmmoData> {
  const columnDef: ColumnDef<MergedAmmoData> = {
    accessorKey,
    header: createSortableHeader(label),
  };
  
  // Set size: use explicit size if provided, otherwise calculate from label
  if (options?.size !== undefined) {
    columnDef.size = options.size;
  } else {
    columnDef.size = calculateColumnWidth(label);
  }
  
  if (options?.cell) {
    columnDef.cell = ({ row }) => options.cell!(row.getValue(accessorKey as string), row.original);
  }

  if (options?.filterFn) {
    columnDef.filterFn = options.filterFn;
  } else {
    const filterType = WEAPON_COLUMN_TYPES[accessorKey as string];
    if (filterType === "number") {
      columnDef.filterFn = filterNumber;
    } else {
      columnDef.filterFn = filterString;
    }
  }
  
  if (options?.meta) {
    columnDef.meta = options.meta;
  }
  
  return columnDef;
}

// Helper function to create a sortable column with custom accessor
function createSortableColumnWithAccessor(
  id: string,
  accessorFn: (row: MergedAmmoData) => unknown,
  label: string,
  options?: { 
    cell?: (ammo: MergedAmmoData) => React.ReactNode;
    filterFn?: FilterFn<MergedAmmoData>;
    size?: number;
    meta?: Record<string, unknown>;
  }
): ColumnDef<MergedAmmoData> {
  const columnDef: ColumnDef<MergedAmmoData> = {
    id,
    accessorFn,
    header: createSortableHeader(label),
  };
  
  // Set size: use explicit size if provided, otherwise calculate from label
  if (options?.size !== undefined) {
    columnDef.size = options.size;
  } else {
    columnDef.size = calculateColumnWidth(label);
  }
  
  if (options?.cell) {
    columnDef.cell = ({ row }) => options.cell!(row.original);
  }

  if (options?.filterFn) {
    columnDef.filterFn = options.filterFn;
  } else {
    const filterType = WEAPON_COLUMN_TYPES[id];
    if (filterType === "number") {
      columnDef.filterFn = filterNumber;
    } else {
      columnDef.filterFn = filterString;
    }
  }
  
  if (options?.meta) {
    columnDef.meta = options.meta;
  }
  
  return columnDef;
}

// Column definitions - Ammo fields
export function createWeaponColumns(): ColumnDef<MergedAmmoData>[] {
  return [
    // Basic ammo info
    createSortableColumn("name", "Ammo Name", {
      meta: { sticky: true },
      cell: (value, ammo) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to={`/weapons/${encodeURIComponent(ammo.descriptorName)}`}
              className="font-medium text-primary hover:underline"
            >
              {value as string}
            </Link>
          </TooltipTrigger>
          {ammo.textureId && (
            <TooltipContent className="p-2">
              <WeaponImage
                textureId={ammo.textureId}
                alt={ammo.name}
                size="custom"
                className="max-w-[200px] max-h-[200px]"
                showPlaceholder={false}
                disableFilter={true}
              />
            </TooltipContent>
          )}
        </Tooltip>
      ),
    }),
    createSortableColumn("descriptorName", "Ammo Descriptor"),
    createSortableColumn("heDamage", "HE Damage"),
    createSortableColumn("minMaxCategory", "Min/Max Category"),


    createSortableColumn("penetration", "Penetration"),
    // Damage fields

    createSortableColumn("heDamageRadius", "HE Damage Radius", {
      cell: (value) => formatDistance(value as number | null | undefined),
    }),
    createSortableColumn("suppress", "Suppress"),
    createSortableColumn("suppressDamagesRadius", "Suppress Damage Radius", {
      cell: (value) => formatDistance(value as number | null | undefined),
    }),
    
    // Range fields
    createSortableColumn("groundMaxRange", "Ground Max Range", {
      cell: (value) => formatDistance(value as number | null | undefined),
    }),
    createSortableColumn("groundMinRange", "Ground Min Range", {
      cell: (value) => formatDistance(value as number | null | undefined),
    }),
    createSortableColumn("heliMaxRange", "Heli Max Range", {
      cell: (value) => formatDistance(value as number | null | undefined),
    }),
    createSortableColumn("heliMinRange", "Heli Min Range", {
      cell: (value) => formatDistance(value as number | null | undefined),
    }),
    createSortableColumn("planeMaxRange", "Plane Max Range", {
      cell: (value) => formatDistance(value as number | null | undefined),
    }),
    createSortableColumn("planeMinRange", "Plane Min Range", {
      cell: (value) => formatDistance(value as number | null | undefined),
    }),
    
    // Timing fields
    createSortableColumn("aimingTime", "Aiming Time", {
      cell: (value) => formatTime(value as number | null | undefined),
    }),
    createSortableColumn("reloadTime", "Reload Time", {
      cell: (value) => formatTime(value as number | null | undefined),
    }),
    createSortableColumn("salvoLength", "Salvo Length"),
    createSortableColumn("timeBetweenSalvos", "Time Between Shots", {
      cell: (value) => formatTime(value as number | null | undefined),
    }),
    createSortableColumn("ammunitionPerSalvo", "Ammunition Per Salvo"),
    createSortableColumn("rateOfFire", "Rate of Fire"),
    createSortableColumn("trueRateOfFire", "True Rate of Fire"),
    createSortableColumn("supplyCostPerSalvo", "Supply Cost Per Salvo"),
    
    // Accuracy fields
    createSortableColumn("staticAccuracy", "Static Accuracy"),
    createSortableColumn("movingAccuracy", "Moving Accuracy"),
    
    // Damage properties
    createSortableColumn("damageDropOff", "Damage Drop Off"),
    createSortableColumn("damageFamily", "Damage Family"),
    createSortableColumn("damageIndex", "Damage Index"),
    
    // Weapon properties
    createSortableColumn("piercingWeapon", "Piercing Weapon", {
      cell: (value) => renderBoolean(value as boolean | undefined),
    }),
    createSortableColumn("tandemCharge", "Tandem Charge", {
      cell: (value) => renderBoolean(value as boolean | undefined),
    }),
    createSortableColumn("isKinetic", "Is Kinetic", {
      cell: (value) => renderBoolean(value as boolean | undefined),
    }),
    
    // Other properties
    createSortableColumn("firesLeftToRight", "Fires Left to Right", {
      cell: (value) => renderBoolean(value as boolean | undefined),
    }),
    createSortableColumn("distanceToTarget", "Distance to Target", {
      cell: (value) => renderBoolean(value as boolean | undefined),
    }),
    createSortableColumn("noiseMalus", "Noise Malus"),
    createSortableColumn("shotsBeforeMaxNoise", "Shots Before Max Noise"),
    createSortableColumn("numberOfSimultaneousProjectiles", "Number of Simultaneous Projectiles"),
  ];
}

